import { Locale } from '@core/constants/locales.constants';
import { extractSlugsFromContentfulResponse } from '@contentful/paths/extractSlugsFromContentfulResponse.utils';
import { mapContentTypesWithRoutes } from '@contentful/paths/mapContentTypesWithRoutes.utils';
import { generateFullPathname } from '@utils/paths/generateFullPathname.utils';
import { Link, LinkInternalLink } from '@graphql/__generated__/graphql';
import cloneDeep from 'lodash/cloneDeep';
import { isObject } from '@core/types/typeGuards/common.typeGuards';
import { CleanContentful } from '@stadionhq-v3/clean-contentful';
import { ContentType } from '@core/constants/contentTypes.constants';
import { Page } from '@core/types/content/contentTypes.types';

type LinkWithInternalLink = CleanContentful<Link> & {
  internalLinkData: CleanContentful<LinkInternalLink> & Page;
};

const isLinkWithInternalLink = (
  value: unknown
): value is LinkWithInternalLink =>
  isObject(value) &&
  value &&
  '__typename' in value &&
  value.__typename === 'Link' &&
  'internalLinkData' in value &&
  isObject(value.internalLinkData);

/**
 * Generates a URL for an internal link based on its __typename property.
 * It utilizes the `mapContentTypesWithRoutes`, `extractSlugsFromContentfulResponse` and `generateFullPathname`
 * functions in order to construct the complete URL, which is then added back to the provided `value`
 * object under the `internalLink.url` key.
 *
 * @param {Record<string, unknown>} value - This object needs to contain an `internalLinkData` key which is
 * presumed to be of `LinkInternalLink` type, else the function returns the argument unchanged.
 * @param {Locale} locale - Locale to use for the URL generation.
 *
 * @returns {InternalLink} The original `value` object, potentially modified by adding a fully constructed URL
 * in the `url` field.
 *
 * @example
 * const internalLinkValue = {
 *   internalLink: {
 *     __typename: "ArticlePage",
 *     slug: "my-article"
 *   }
 * };
 * const locale = "US";
 * const result = constructInternalLink(internalLinkValue, locale);
 * // Outputs:
 * // {
 * //   ...internalLinkValue
 * //   url: "https://example-domain.com/US/news/[my-article]"
 * // }
 *
 */
function constructInternalLink(
  value: LinkWithInternalLink,
  locale: Locale
): CleanContentful<Link> {
  const { internalLinkData, subPage, ...rest } = value;

  const typename: ContentType | undefined = internalLinkData?.__typename;

  if (typename === undefined) {
    throw new Error('Internal Link has no typename');
  }

  const params = extractSlugsFromContentfulResponse(internalLinkData);

  const route = mapContentTypesWithRoutes(typename, {
    params,
    subPage,
  });

  const url = generateFullPathname({
    locale,
    route,
    params,
  });

  return {
    ...rest,
    url,
  };
}

export function generateInternalLinkUrl(
  data: CleanContentful<Link>,
  locale: Locale
): CleanContentful<Link> | null {
  // Deep copy the input data to avoid mutating original data
  const copiedData: CleanContentful<Link> = cloneDeep(data);

  if (isLinkWithInternalLink(copiedData)) {
    return constructInternalLink(copiedData, locale);
  }

  return data;
}
